import React, { useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';

type MaxWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Header title. Omit to render a headerless modal. */
  title?: React.ReactNode;
  /** Optional icon shown left of the title. */
  icon?: React.ReactNode;
  /** Tailwind classes for the header background tint (e.g. "bg-indigo-50/50"). */
  headerAccent?: string;
  children: React.ReactNode;
  /** Optional footer area, rendered with a top border. */
  footer?: React.ReactNode;
  maxWidth?: MaxWidth;
  /** When false the body is not wrapped in padding (caller controls layout). */
  bodyPadding?: boolean;
}

const MAX_WIDTH: Record<MaxWidth, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  icon,
  headerAccent = 'bg-slate-50/70',
  children,
  footer,
  maxWidth = 'md',
  bodyPadding = true,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        const nodes = Array.from(
          panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
        ).filter((el) => el.offsetParent !== null);
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement;
    document.addEventListener('keydown', handleKeyDown, true);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // Focus the first focusable element inside the panel.
    const raf = requestAnimationFrame(() => {
      const node = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      node?.focus();
    });

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = overflow;
      cancelAnimationFrame(raf);
      previouslyFocused.current?.focus?.();
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={`bg-white rounded-2xl shadow-xl border border-slate-200 w-full ${MAX_WIDTH[maxWidth]} max-h-[85vh] overflow-hidden flex flex-col animate-slide-up`}
        onClick={(e) => e.stopPropagation()}
      >
        {title !== undefined && (
          <div
            className={`px-6 py-4 border-b border-slate-100 ${headerAccent} flex items-center justify-between flex-shrink-0`}
          >
            <h3 className="text-base font-semibold text-slate-800 flex items-center">
              {icon && <span className="mr-2 flex items-center">{icon}</span>}
              {title}
            </h3>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        )}

        <div className={`overflow-y-auto ${bodyPadding ? 'p-6' : ''}`}>{children}</div>

        {footer && (
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-2 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
